import { IVehiclePricingMatrix } from "../../../../models/account/IAccountPartPricingMatrix";
import { PhysicalRouting } from "../../../../models/Part";

export interface IBestPriceOption {
  accountId: string;
  accountName: string;
  accountType: number;
  price: number;
  physicalRoutingDefault: PhysicalRouting;
  /** The £0 is the account's standing default for the category, not a price anyone typed */
  autoSelect?: boolean;
}

export interface IBestMatrixPrice {
  bestOption: IBestPriceOption;
  options: IBestPriceOption[];
}

export type BestMatrixPriceByCategoryId = Map<string, IBestMatrixPrice>;

/**
 * Flattens the vehicle pricing matrix into the best (highest) enabled price per category,
 * with the remaining options kept for the "other prices" picker.
 *
 * A cell linked as auto-select counts as a £0 option even with nothing typed in it: that is
 * the account's standing offer for the category (e.g. CATs to Reintegration), so the part is
 * always created and labelled. Any real price still beats it, and on a tie a typed price
 * comes before a default.
 *
 * Extracted from LeadPartsCategoriesTable so CreateJobs can make the same
 * "does this category have a price?" judgement globally - a main category and its sub-parts
 * often sit in different tables, so that decision can't be made inside one table.
 */
export function buildBestMatrixPriceByCategoryId(
  matrix: IVehiclePricingMatrix | undefined | null
): BestMatrixPriceByCategoryId {
  const bestByCategory: BestMatrixPriceByCategoryId = new Map();

  if (!matrix) return bestByCategory;

  const accountById = new Map(
    matrix.accounts.map((account) => [
      account.accountId,
      {
        accountName: account.accountName,
        accountType: account.accountType,
        physicalRoutingDefault: account.physicalRoutingDefault ?? PhysicalRouting.None,
      },
    ])
  );

  matrix.categories.forEach((category) => {
    const options: IBestPriceOption[] = [];

    category.cells.forEach((cell) => {
      if (!cell.enabled) {
        return;
      }

      const isDefault = cell.price == null && !!cell.autoSelect;
      if (cell.price == null && !isDefault) {
        return;
      }

      const account = accountById.get(cell.accountId);
      if (!account) {
        return;
      }

      options.push({
        accountId: cell.accountId,
        price: cell.price ?? 0,
        accountName: account.accountName,
        accountType: account.accountType,
        physicalRoutingDefault: account.physicalRoutingDefault,
        autoSelect: isDefault,
      });
    });

    if (!options.length) {
      return;
    }

    const sortedOptions = [...options].sort(
      (a, b) => b.price - a.price || Number(!!a.autoSelect) - Number(!!b.autoSelect)
    );
    bestByCategory.set(category.partCategoryId, {
      bestOption: sortedOptions[0],
      options: sortedOptions,
    });
  });

  return bestByCategory;
}
